import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { agentAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import { ArrowLeft, Play, Shield, Mail, Bug, KeyRound, Skull, Network, Eye, Lock, Globe, Code, Cloud, ClipboardCheck, Zap, GraduationCap, FileText, ShieldAlert, Siren, Search } from 'lucide-react';

const ICON_MAP = {
  Shield, Mail, Bug, KeyRound, Skull, Network, Eye, Lock, Globe, Code, Cloud,
  ClipboardCheck, Zap, GraduationCap, FileText, ShieldAlert, Siren, Search,
};

export default function AgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    agentAPI.get(agentId).then(res => {
      setAgent(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [agentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020202]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-[#020202]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <p className="font-mono text-[#ff003c]">AGENT NOT FOUND</p>
        </div>
      </div>
    );
  }

  const IconComp = ICON_MAP[agent.icon] || Shield;

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="agent-detail-page">
      <Navbar />
      <div className="pt-20 px-6 md:px-12 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-[#999] hover:text-[#FF7A3D] transition-colors mb-8 font-mono text-xs" data-testid="back-to-marketplace">
            <ArrowLeft className="w-4 h-4" />
            BACK TO MARKETPLACE
          </Link>

          <div className="card-cyber rounded-sm overflow-hidden">
            {/* Header */}
            <div className="p-8 border-b border-white/5">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-sm flex items-center justify-center bg-[#FF7A3D]/10 border border-[#FF7A3D]/20 flex-shrink-0">
                  <IconComp className="w-8 h-8 text-[#FF7A3D]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{agent.name}</h1>
                    <Badge className={`font-mono text-[10px] uppercase ${agent.is_active ? 'bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20' : 'bg-[#555]/10 text-[#aaa] border-[#555]/20'}`}>
                      {agent.is_active ? 'ACTIVE' : 'COMING SOON'}
                    </Badge>
                    <Badge className="bg-[#00E5C1]/10 text-[#00E5C1] border-[#00E5C1]/20 font-mono text-[10px] uppercase">
                      {agent.tier}
                    </Badge>
                  </div>
                  <p className="font-body text-[#888] leading-relaxed">{agent.description}</p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card-cyber p-4 rounded-sm">
                <p className="font-mono text-xs text-[#999] tracking-widest mb-1">COST PER RUN</p>
                <p className="font-heading text-2xl font-bold text-[#FF7A3D]">{agent.price_per_run} <span className="text-sm text-[#999]">credits</span></p>
              </div>
              <div className="card-cyber p-4 rounded-sm">
                <p className="font-mono text-xs text-[#999] tracking-widest mb-1">CATEGORY</p>
                <p className="font-heading text-lg font-bold text-white uppercase">{agent.category.replace('_', ' ')}</p>
              </div>
              <div className="card-cyber p-4 rounded-sm">
                <p className="font-mono text-xs text-[#999] tracking-widest mb-1">STATUS</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`w-2 h-2 rounded-full ${agent.is_active ? 'bg-[#FF7A3D] pulse-green' : 'bg-[#555]'}`} />
                  <span className={`font-heading text-lg font-bold ${agent.is_active ? 'text-[#FF7A3D]' : 'text-[#aaa]'}`}>
                    {agent.is_active ? 'ONLINE' : 'OFFLINE'}
                  </span>
                </div>
              </div>
            </div>

            {/* Action */}
            <div className="p-8 border-t border-white/5">
              {agent.is_active ? (
                <Button
                  onClick={() => {
                    if (!user) { navigate('/auth'); return; }
                    navigate(`/agents/${agent.agent_id}/console`);
                  }}
                  className="bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest hover:bg-[#FF7A3D]/90 hover:shadow-[0_0_20px_rgba(255,122,61,0.4)] transition-all rounded-sm h-12 px-8"
                  data-testid="run-agent-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> RUN AGENT
                </Button>
              ) : (
                <div className="flex items-center gap-4">
                  <Button disabled className="bg-white/5 text-[#aaa] font-heading uppercase tracking-widest rounded-sm h-12 px-8 cursor-not-allowed" data-testid="agent-coming-soon-btn">
                    COMING SOON
                  </Button>
                  <p className="font-mono text-xs text-[#aaa]">This agent is under development</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
